import type { Product } from '../types';
import type { AudienceFace } from './audience';

type BookAudience = 'kids' | 'adults' | 'public';

const kidsCategories = new Set(['children', 'child', 'kids', 'kid', 'adventure', 'adventures']);
const adultCategories = new Set(['business', 'history', 'poetry', 'adult', 'adults']);

const normalizeCategoryToken = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^products\.categories\./, '')
    .replace(/[_\s]+/g, '-');

const audiencePriorityByFace: Record<AudienceFace, BookAudience[]> = {
  kids: ['kids', 'public', 'adults'],
  adults: ['adults', 'public', 'kids'],
  public: ['public', 'kids', 'adults'],
};

const getProductAudience = (product: Product): BookAudience => {
  if (product.audience === 'kids' || product.audience === 'adults' || product.audience === 'public') {
    return product.audience;
  }

  const normalizedCategory = normalizeCategoryToken(product.categoryKey);
  if (kidsCategories.has(normalizedCategory)) {
    return 'kids';
  }

  if (adultCategories.has(normalizedCategory)) {
    return 'adults';
  }

  return 'public';
};

export const sortProductsByAudienceFace = (products: Product[], face: AudienceFace): Product[] => {
  const priority = audiencePriorityByFace[face];
  return [...products].sort((firstProduct, secondProduct) => {
    const firstAudience = getProductAudience(firstProduct);
    const secondAudience = getProductAudience(secondProduct);
    const audienceOrder = priority.indexOf(firstAudience) - priority.indexOf(secondAudience);
    if (audienceOrder !== 0) {
      return audienceOrder;
    }

    return secondProduct.rating - firstProduct.rating;
  });
};

export const isKidsAudienceBook = (product: Product): boolean => getProductAudience(product) === 'kids';
